import { useSyncExternalStore } from 'react';
import { CapabilitiesResponse, fetchCapabilities } from './api';

export interface Capabilities {
  canCreate?: boolean;
  canManageScopes?: boolean;
}

let capabilities: Capabilities = {};
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

export function setCapabilities(next: Capabilities): void {
  if (capabilities.canCreate === next.canCreate && capabilities.canManageScopes === next.canManageScopes) {
    return;
  }
  capabilities = { ...capabilities, ...next };
  emit();
}

export function getCapabilities(): Capabilities {
  return capabilities;
}

export function resetCapabilities(): void {
  capabilities = {};
  cached = null;
  inflight = null;
  generation += 1;
  emit();
}

let cached: CapabilitiesResponse | null = null;
let inflight: Promise<CapabilitiesResponse> | null = null;
let generation = 0;

export async function loadCapabilities(): Promise<CapabilitiesResponse> {
  if (cached) return cached;
  const gen = generation;
  if (!inflight) {
    inflight = (async () => {
      const caps = await fetchCapabilities();
      if (gen === generation) {
        cached = caps;
        setCapabilities({ canCreate: caps.canCreate, canManageScopes: caps.canManageScopes });
      }
      return caps;
    })();
  }
  const pending = inflight;
  try {
    return await pending;
  } catch (err) {
    // Only clear the memo if no reset/newer load has superseded this one.
    if (gen === generation) inflight = null;
    throw err;
  }
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useCapabilities(): Capabilities {
  return useSyncExternalStore(subscribe, getCapabilities);
}
